import React from "react";
import Link from "next/link";
import { GetStaticProps, InferGetStaticPropsType } from "next";
import { NextSeo } from "next-seo";
import { RichText } from "prismic-reactjs";
import styles from "src/styles/press.scss";
import { EXTRAS_CONTENT_QUERY } from "src/gql/queries";
import { initializeApollo } from "src/lib/apollo-client";
import { useLocalDate } from "src/services/DateService";
import { hasHeroContent, renderHeroRichText, renderRichText } from "src/services/PrismicService";
import { getArticleAsUrl } from "src/services/UrlService";
import { getPressReleases, PressReleaseListItem } from "src/services/PressReleaseService";

const PAGE_TITLE = "Press Releases | BetQL";
const EXTRAS_UID = "press";

interface PressExtras {
  content: any;
  seo_description: string | null;
  seo_h1: string | null;
  seo_h2: string | null;
  seo_hero_content: any;
  seo_title: string | null;
}

const PressReleaseItem = ({ pressRelease }: { pressRelease: PressReleaseListItem }) => {
  const date = useLocalDate(pressRelease._meta.firstPublicationDate, "MMM D, YYYY");
  return (
    <Link href={getArticleAsUrl(pressRelease)} legacyBehavior>
      <a className="press-release-item">
        <div className="press-release-date">{date}</div>
        <div className="press-release-title">{RichText.asText(pressRelease.title)}</div>
        {pressRelease.excerpt && <div className="press-release-excerpt">{pressRelease.excerpt}...</div>}
      </a>
    </Link>
  );
};

export const getStaticProps = (async ({ previewData }) => {
  const context = previewData ? { headers: { "Prismic-ref": (previewData as { ref: string })?.ref } } : {};
  const [pressReleases, extrasResponse] = await Promise.all([
    getPressReleases(),
    initializeApollo().query<{ prismicExtras: PressExtras | null }, { uid: string }>({
      query: EXTRAS_CONTENT_QUERY,
      variables: { uid: EXTRAS_UID },
      context,
    }),
  ]);
  return {
    props: { pressReleases, extras: extrasResponse?.data?.prismicExtras ?? null },
    revalidate: 10 * 60, // 10 mins
  };
}) satisfies GetStaticProps<{ pressReleases: PressReleaseListItem[]; extras: PressExtras | null }>;

const PressReleases = ({ pressReleases, extras }: InferGetStaticPropsType<typeof getStaticProps>) => {
  const title = extras?.seo_title || PAGE_TITLE;
  const description = extras?.seo_description || undefined;
  const heroContent = extras?.seo_hero_content;
  const content = extras?.content;
  return (
    <div className="press-page-wrapper">
      <div className="press-page">
        <NextSeo
          title={title}
          description={description}
          openGraph={{ url: "https://www.betql.co/press", title, description }}
        />
        <div className="press-page-header">
          {hasHeroContent(heroContent) ? (
            renderHeroRichText(heroContent)
          ) : (
            <>
              <h1 className="press-page-title">{extras?.seo_h1 || "Press Releases"}</h1>
              {extras?.seo_h2 && <h2 className="press-page-subtitle">{extras.seo_h2}</h2>}
            </>
          )}
        </div>
        <div className="press-release-list">
          {pressReleases.map((pressRelease) => (
            <PressReleaseItem key={pressRelease._meta.id} pressRelease={pressRelease} />
          ))}
          {!pressReleases.length && <div className="press-release-empty">No press releases found.</div>}
        </div>
        {hasHeroContent(content) && <div className="press-page-content">{renderRichText(content)}</div>}
      </div>
      <style jsx global>
        {styles}
      </style>
    </div>
  );
};

export default PressReleases;
